import type { Contact, OwedEntry, SettlementSummary } from '@/api/people'
import { formatDate, formatShortDate } from '@/lib/formatDate'

export type PeopleSort = 'biggest' | 'recent' | 'name'

const paise = (n: number) => Math.round(n * 100)

/** Positive: they owe you. Negative: you owe them. */
export function netOf(c: Pick<Contact, 'owedToUs' | 'weOwe'>): number {
  return (paise(c.owedToUs) - paise(c.weOwe)) / 100
}

export function totalsOf(contacts: Contact[]) {
  const active = contacts.filter((c) => !c.archived)
  const owedToUs = active.reduce((s, c) => s + paise(c.owedToUs), 0)
  const weOwe = active.reduce((s, c) => s + paise(c.weOwe), 0)
  return {
    owedToUs: owedToUs / 100,
    weOwe: weOwe / 100,
    net: (owedToUs - weOwe) / 100,
    owedToUsCount: active.filter((c) => c.owedToUs > 0).length,
    weOweCount: active.filter((c) => c.weOwe > 0).length,
  }
}

export function isSettled(c: Contact): boolean {
  return c.owedToUs === 0 && c.weOwe === 0
}

const byName = (a: Contact, b: Contact) => a.name.localeCompare(b.name)

const comparators: Record<PeopleSort, (a: Contact, b: Contact) => number> = {
  biggest: (a, b) => Math.abs(netOf(b)) - Math.abs(netOf(a)) || byName(a, b),
  recent: (a, b) => (b.lastActivityOn ?? '').localeCompare(a.lastActivityOn ?? '') || byName(a, b),
  name: byName,
}

export function groupPeople(contacts: Contact[], search: string, sort: PeopleSort) {
  const q = search.trim().toLowerCase()
  const matching = contacts.filter((c) => c.name.toLowerCase().includes(q)).sort(comparators[sort])
  return {
    open: matching.filter((c) => !c.archived && !isSettled(c)),
    settled: matching.filter((c) => !c.archived && isSettled(c)),
    archived: matching.filter((c) => c.archived),
  }
}

export type HistoryItem =
  | { kind: 'entry'; id: string; date: string; entry: OwedEntry }
  | { kind: 'settlement'; id: string; date: string; settlement: SettlementSummary }

export interface HistoryMonth {
  label: string
  items: HistoryItem[]
}

/** Settled entries and settlements, newest month first. Open entries live in the Open list instead. */
export function buildHistory(
  entries: OwedEntry[],
  settlements: SettlementSummary[]
): HistoryMonth[] {
  const items: HistoryItem[] = [
    ...entries
      .filter((e) => e.outstanding === 0)
      .map((e): HistoryItem => ({ kind: 'entry', id: e.id, date: e.entryDate, entry: e })),
    ...settlements.map(
      (s): HistoryItem => ({ kind: 'settlement', id: s.id, date: s.settledOn, settlement: s })
    ),
  ]
  // A settlement comes after the entries it cleared, so on the same day it reads first (newest first).
  const rank = (i: HistoryItem) => (i.kind === 'settlement' ? 0 : 1)
  items.sort((a, b) => b.date.localeCompare(a.date) || rank(a) - rank(b))

  const months: HistoryMonth[] = []
  for (const item of items) {
    const label = new Date(item.date.slice(0, 7) + '-01T00:00:00').toLocaleDateString('en-IN', {
      month: 'long',
      year: 'numeric',
    })
    const last = months[months.length - 1]
    if (last?.label === label) last.items.push(item)
    else months.push({ label, items: [item] })
  }
  return months
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')
}

export function formatActivity(date: string): string {
  return date.slice(0, 4) === String(new Date().getFullYear())
    ? formatShortDate(date)
    : formatDate(date)
}

export function sourceLabel(e: OwedEntry, name: string): string {
  if (e.sourceType === 'OPENING') return 'Owed before you started'
  if (e.sourceType === 'PERSON_PAYMENT') return e.direction === 'OWED_TO_US' ? 'Lent' : 'Borrowed'
  return e.direction === 'OWED_TO_US' ? 'Shared cost' : `${name} paid for you`
}
